/**
 * Design Tokens — Single Source of Truth
 *
 * This file defines the centralized design system for the entire application.
 * It feeds into tailwind.config.js (via design-tokens.cjs) and is available
 * as Tailwind utility classes.
 *
 * To change a color globally, change it HERE — it propagates everywhere.
 *
 * Naming convention uses fg/surface/line prefixes for clean Tailwind classes:
 *   text-fg, text-fg-secondary, bg-surface, bg-surface-hover, border-line, etc.
 *
 * For React components that need JS access, import from tokens.ts instead.
 */

export const colors = {
  // ─── Foreground (text & icons) ──────────────────────────────────
  // Usage: text-fg, text-fg-secondary, text-fg-muted
  fg: '#191D23', // Primary text — headings, body copy
  'fg-secondary': '#6E6E6E', // Supporting text — labels, descriptions
  'fg-tertiary': '#475467', // Subtle text — table headers, metadata
  'fg-muted': '#C4C4C4', // Placeholder, disabled text
  'fg-dark': '#101828', // Extra emphasis — dark headings
  'fg-subtle': '#414942', // UI controls — drag handles, arrows
  'fg-inverse': '#FFFFFF', // Text on dark backgrounds

  // ─── Brand ──────────────────────────────────────────────────────
  // Usage: text-brand, bg-brand, border-brand
  brand: '#06512F', // Primary brand green
  'brand-hover': '#2C6B4E', // Hover state
  'brand-active': '#087443', // Active/pressed state
  'brand-pressed': '#054628', // Button pressed state
  'brand-dark': '#245A42', // Hover on dark brand buttons
  'brand-deep': '#002110', // Darkest green — visited links, deep accents
  'brand-light': '#01e677', // Bright accent (badges, highlights)
  'brand-bright': '#0A9150', // Bright action green — primary action buttons
                             //   on the homepage refresh (Apply, Send, etc.)
  'brand-focus': 'rgba(8, 116, 67, 0.3)', // brand-active @ 30% — focus-visible ring

  // ─── Brand Basil scale ──────────────────────────────────────────
  // Full brand green palette. Use scale tokens (`bg-basil-100`, etc.)
  // when picking off a Figma swatch by number; prefer the semantic
  // tokens above (`bg-brand`, `text-brand-active`) for general UI.
  // Note: basil-700 == brand-active.
  'basil-50': '#EDFCF2',
  'basil-100': '#D2F9DE',
  'basil-200': '#AAF0C4',
  'basil-300': '#72E3A3',
  'basil-400': '#39CE7E',
  'basil-500': '#16B364',
  'basil-600': '#0A9150',
  'basil-700': '#087443', // Primary — same value as brand-active
  'basil-800': '#095C37',
  'basil-900': '#084C2F',
  'basil-950': '#032B1B',

  // ─── Brand Sage scale ───────────────────────────────────────────
  // Distinct from the legacy `sage` palette in tailwind.config.js;
  // those values are pre-design-system and slightly different
  // (e.g. legacy sage.50 = #F4F7F5 vs brand-sage-50 = #F5F8F6).
  'brand-sage-50': '#F5F8F6',
  'brand-sage-100': '#DDEAE3',
  'brand-sage-200': '#BAD5C6',
  'brand-sage-300': '#90B8A3',
  'brand-sage-400': '#5F8C76',
  'brand-sage-500': '#4E7E67',
  'brand-sage-600': '#3D6352',
  'brand-sage-700': '#345144',
  'brand-sage-800': '#2C4339',
  'brand-sage-900': '#283932',
  'brand-sage-950': '#13201B',

  // ─── Opacity values ─────────────────────────────────────────────
  // The Figma design system documents discrete opacity swatches like
  // "Brand Basil 700 @ 15%" or "Gray @ 30%". We do NOT mirror these
  // as named tokens because Tailwind's `/<N>` modifier handles them
  // natively against any color token, producing the exact RGBA:
  //
  //   bg-basil-700/15        →  rgba(8, 116, 67, 0.15)
  //   text-gray-500/30       →  rgba(107, 114, 128, 0.30)
  //   bg-brand-sage-500/5    →  rgba(78, 126, 103, 0.05)
  //   bg-white/0             →  transparent white (Figma's "0%")
  //
  // When porting a Figma swatch, translate "Color @ N%" to
  // "<utility>-<color-name>/N" directly. No token lookup needed.

  'green-100': '#DCFCE7', // Tailwind green-100 — subtle brand-tinted fill (avatar initials backdrop, etc.)

  // ─── Neutral grays (Tailwind v3 scale) ─────────────────────────
  // Usage: text-neutral-800, bg-neutral-100, border-neutral-300
  // These match the Tailwind default gray scale, used by newer cards
  // that originate from Figma designs built on Tailwind defaults rather
  // than the project's sage-tinted `fg` palette. Prefer the semantic
  // `fg-*`/`surface-*`/`line` tokens for general UI chrome — these exist
  // for cases where matching a Figma value exactly matters.
  'neutral-50': '#F9FAFB', // gray-50 — lightest neutral fill
  'neutral-100': '#F3F4F6', // gray-100 — pill-tab track, hover surfaces
  'neutral-200': '#E5E7EB', // gray-200 — popover drop-shadow border (FbDropdown)
  'neutral-300': '#D1D5DB', // gray-300 — input/button borders
  'neutral-400': '#9CA3AF', // gray-400 — hover border darken
  'neutral-500': '#6B7280', // gray-500 — secondary text on light surfaces
  'neutral-600': '#4B5563', // gray-600 — mid-emphasis text
  'neutral-700': '#374151', // gray-700 — body text darken on hover
  'neutral-800': '#1F2937', // gray-800 — primary text on light surfaces
  'neutral-900': '#111827', // gray-900 — emphasis text on hover
  'neutral-950': '#030712', // gray-950 — highest-contrast text/icons

  // ─── Surfaces (backgrounds) ─────────────────────────────────────
  // Usage: bg-surface, bg-surface-hover, bg-surface-muted
  surface: '#FFFFFF', // Page and card backgrounds
  'surface-bright': '#FCFCFC', // Near-white page background — barely off pure white
  'surface-page': '#FEFEFE', // Near-white page background, a hair brighter than surface-bright
  'surface-alt': '#F5F5F5', // Alternate surface
  'surface-alt-active': '#E7E7E7', // Pressed state on alt surfaces
  'surface-raised': '#FAFAFA', // Subtle raised surface
  'surface-muted': '#F4F7F5', // Card backgrounds (sage-50)
  'surface-hover': '#EEF5F2', // Hover state (pistachio)
  'surface-hover-deep': '#E5F2EA', // Deeper hover — hover-on-hover elements
  'surface-active': '#D6E8E0', // Pressed/selected state
  'surface-selected': '#D6E2DA', // Selected row — email inbox table (Figma exact; slightly different shade from surface-active)
  'surface-row-hover': '#F6F9F7', // DS2026 v2 table row hover (5% sage on white)
  'surface-row-selected': '#E4ECE8', // DS2026 v2 table selected row (15% sage on white)
  'surface-row-selected-hover': '#DCE5E1', // DS2026 v2 table selected + hover (20% sage on white)
  'surface-disabled': '#E2E2E2', // Disabled elements
  'surface-sunken': '#F9FAFB', // Recessed areas

  // ─── Lines (borders & dividers) ─────────────────────────────────
  // Usage: border-line, border-line-form, ring-line-focus
  line: '#E2E2E2', // Standard borders
  'line-form': '#DEE2E6', // Form input borders
  'line-subtle': '#EAECF0', // Table dividers, light borders
  'line-focus': '#8EA69B', // Input focus ring (sage)
  'line-row-separator': '#dadce0', // Gmail-style row elevation shadow border (no Tailwind shorthand — use hex in shadow-[...])
  'line-brand': '#06512F', // Brand accent borders

  // ─── Navigation (dark background context) ───────────────────────
  // Usage: bg-nav-bg, text-nav-text
  'nav-bg': '#06512F',
  'nav-bg-hover': '#2C6B4E',
  'nav-text': '#FFFFFF',
  'nav-text-muted': '#C1D1C6',
  'nav-tooltip-bg': '#D6E2DA',
  'nav-tooltip-text': '#191D23',

  // ─── Status ─────────────────────────────────────────────────────
  // Usage: text-error, bg-error-bg, border-error-border
  error: '#D43D51',
  'error-bg': '#FDECEC',
  'error-bg-subtle': '#fef2f2', // Very light red tint — badge backgrounds
  'error-bg-hover': '#FEE2E2', // red-100 — hover state for subtle red surfaces (destructive menu items)
  'error-border': '#E87676',
  'error-strong': '#B91C1C', // red-700 — emphasis text on light red backgrounds (urgency badges)
  success: '#059669',
  'success-bright': '#28B643', // Bright success green — offers, highlights
  'success-bg': '#D1FAE5',
  'success-bg-subtle': '#f0fdf4', // Very light green tint — badge backgrounds
  'success-bg-tint': '#DCFCE7', // green-100 — light-green fill behind success icons/tiles (sits one step above success-bg-subtle's green-50)
  'success-strong': '#15803D', // green-700 — emphasis text on light green backgrounds (DS2026 badge text)
  warning: '#B45309', // amber-700 — passes AA as text (5.02:1 on white). Was amber-600 #D97706 (3.19:1, failed). Used as text/icons + small status dots/bars.
  'warning-bg': '#FEF3C7',
  'warning-bg-subtle': '#fffbeb', // Very light amber tint — badge backgrounds
  'warning-border': '#F2C66D',
  'warning-strong': '#B45309', // amber-700 — emphasis text on light amber backgrounds
  info: '#2563EB',
  'info-bg': '#DBEAFE',
  'info-bg-subtle': '#F0F9FF', // sky-50 — light blue tint, badge backgrounds
  'info-strong': '#0369A1', // sky-700 — emphasis text on light blue backgrounds

  // ─── Alerts (DS2026 Figma node 7172:1687) ───────────────────────
  // Five colors × {bg, border, title, text}. Title is the heavier
  // semibold heading, text is the lighter description body.
  'alert-success-bg': '#f0fdf4', // green-50
  'alert-success-border': '#bbf7d0', // green-200
  'alert-success-title': '#166534', // green-800
  'alert-success-text': '#15803d', // green-700
  'alert-warning-bg': '#fefce8', // yellow-50 (alerts use yellow, badges use amber)
  'alert-warning-border': '#fef08a', // yellow-200
  'alert-warning-title': '#854d0e', // yellow-800
  'alert-warning-text': '#a16207', // yellow-700
  'alert-info-bg': '#f0f9ff', // sky-50
  'alert-info-border': '#bae6fd', // sky-200
  'alert-info-title': '#075985', // sky-800
  'alert-info-text': '#0369a1', // sky-700
  'alert-danger-bg': '#fef2f2', // red-50
  'alert-danger-border': '#fecaca', // red-200
  'alert-danger-title': '#991b1b', // red-800
  'alert-danger-text': '#b91c1c', // red-700
  'alert-default-bg': '#f9fafb', // gray-50
  'alert-default-border': '#e5e7eb', // gray-200
  'alert-default-title': '#1f2937', // gray-800
  'alert-default-text': '#374151', // gray-700

  // ─── Sample Request Status (pill backgrounds & text) ──────────
  // Usage: colors['status-submitted-bg'] in JS, bg-status-submitted-bg in Tailwind
  'status-submitted': '#16A34A',       // Solid pill — submitted badge
  'status-submitted-bg': '#FEF5B4',    // Pill background
  'status-submitted-text': '#463B05',  // Pill text
  'status-cancelled-bg': '#FFD1C9',
  'status-cancelled-text': '#642509',
  'status-pending-bg': '#FEF3C7',      // Same as warning-bg
  'status-pending-text': '#92400E',
  'status-accepted-bg': '#DBEAFE',     // Same as info-bg
  'status-accepted-text': '#1E40AF',
  'status-rejected': '#DC2626',        // Solid pill — rejected badge
  'status-rejected-bg': '#FEE2E2',
  'status-rejected-text': '#991B1B',
  'status-default-bg': '#6B7280',      // Fallback for unknown statuses
  'status-default-text': '#FFFFFF',

  // ─── Workflow Status (campaign status badges) ───────────────────
  // Usage: bg-wf-active-bg text-wf-active-text
  'wf-draft-bg': '#F3F4F6',         // gray-100
  'wf-draft-text': '#374151',       // gray-700
  'wf-requested-bg': '#FFFBEB',     // amber-50
  'wf-requested-text': '#B45309',   // amber-700
  'wf-ready-bg': '#EFF6FF',         // blue-50
  'wf-ready-text': '#1D4ED8',       // blue-700
  'wf-active-bg': '#ECFDF5',        // emerald-50
  'wf-active-text': '#047857',      // emerald-700
  'wf-complete-bg': '#F3F4F6',      // gray-100
  'wf-complete-text': '#4B5563',    // gray-600
  'wf-paused-bg': '#FFF7ED',        // orange-50
  'wf-paused-text': '#C2410C',      // orange-700

  // ─── Overlay ────────────────────────────────────────────────────
  'modal-overlay': 'rgba(4, 30, 18, 0.5)',

  // ─── Design System 2026 (new system, gradual migration) ────────
  // Mirrors the new Figma design system spec 1:1. Use these tokens
  // for any frontend being migrated to the new system; existing
  // semantic tokens (`fg`, `surface`, `line`, …) stay valid until
  // each component is converted. Dark-mode mappings are documented
  // in comments for when dark mode is wired up (currently light-only).

  // Text
  'ds2026-text-brand': '#087443',          // Basil 700 — dark: Basil 500 (#16B364)
  'ds2026-text-neutral-brand': '#087443',  // same as text-brand in light — dark: Basil 500
  'ds2026-text-title': '#1F2937',          // Gray 800 — dark: White @ 90%
  'ds2026-text-body': '#374151',           // Gray 700 — dark: Gray 400
  'ds2026-text-secondary': '#6B7280',      // Gray 500 — dark: Gray 400
  'ds2026-text-tertiary': '#9CA3AF',       // Gray 400 — dark: Gray 500

  // Border
  'ds2026-border-primary': '#F3F4F6',         // Gray 100 — dark: Gray 800
  'ds2026-border-secondary': '#E5E7EB',       // Gray 200 — dark: Gray 600
  'ds2026-border-secondary-alt': '#E5E7EB',   // Gray 200 — dark: Gray 700
  'ds2026-border-tertiary': '#D1D5DB',        // Gray 300 — dark: Gray 700

  // Foreground / Dark — high-contrast icons & shapes
  'ds2026-fg-dark-primary': '#030712',        // Gray 950 — dark: White
  'ds2026-fg-dark-secondary': '#111827',      // Gray 900 — dark: White

  // Foreground / Gray — muted icons & shapes
  'ds2026-fg-gray-primary': '#111827',          // Gray 900 — dark: Gray 50
  'ds2026-fg-gray-secondary': '#111827',        // Gray 900 — dark: Gray 100
  'ds2026-fg-gray-secondary-alt': '#1F2937',    // Gray 800 — dark: Gray 100
  'ds2026-fg-gray-secondary-alt-2': '#030712',  // Gray 950 — dark: Gray 100
  'ds2026-fg-gray-tertiary': '#1F2937',         // Gray 800 — dark: Gray 200
  'ds2026-fg-gray-quaternary': '#374151',       // Gray 700 — dark: Gray 300

  // Background / White
  'ds2026-bg-primary': '#FFFFFF',     // White — dark: Gray 950
  'ds2026-bg-secondary': '#FFFFFF',   // White — dark: Gray 900

  // Background / Gray
  'ds2026-bg-gray-primary': '#F9FAFB',         // Gray 50 — dark: Gray 900
  'ds2026-bg-gray-secondary': '#F3F4F6',       // Gray 100 — dark: Gray 900
  'ds2026-bg-gray-secondary-alt': '#F3F4F6',   // Gray 100 — dark: Gray 800
  'ds2026-bg-gray-secondary-alt-2': '#F3F4F6', // Gray 100 — dark: Gray 950
  'ds2026-bg-gray-tertiary': '#E5E7EB',        // Gray 200 — dark: Gray 800
  'ds2026-bg-gray-quaternary': '#D1D5DB',      // Gray 300 — dark: Gray 700

  // Button / Primary
  'ds2026-btn-primary-bg': '#0A9150',        // Basil 600 — dark: Basil 500
  'ds2026-btn-primary-hover-bg': '#087443',  // Basil 700 — dark: Basil 600
  'ds2026-btn-primary-text': '#FFFFFF',      // White — dark: White

  // Button / Primary Outline
  'ds2026-btn-outline-bg': '#FFFFFF',              // White — dark: Gray 900
  'ds2026-btn-outline-stroke': '#D1D5DB',          // Gray 300 — dark: Gray 800
  'ds2026-btn-outline-hover-bg': '#F3F4F6',        // Gray 100 — dark: Gray 900
  'ds2026-btn-outline-text': '#1F2937',            // Gray 800 — dark: Gray 200
  'ds2026-btn-outline-hover-text': '#111827',      // Gray 900 — dark: Gray 100
  'ds2026-btn-outline-disabled-bg': '#F3F4F6',     // Gray 100 — dark: Gray 800
  'ds2026-btn-outline-disabled-text': '#9CA3AF',   // Gray 400 — dark: Gray 600

  // Button / Disabled / Primary
  'ds2026-btn-disabled-bg': '#F3F4F6',      // Gray 100 — dark: Gray 800
  'ds2026-btn-disabled-border': '#F3F4F6',  // Gray 100 — dark: Gray 800
  'ds2026-btn-disabled-text': '#9CA3AF',    // Gray 400 — dark: Gray 600

  // Button / Disabled / Outline
  'ds2026-btn-disabled-outline-bg': '#FFFFFF',      // White — dark: Gray 800
  'ds2026-btn-disabled-outline-border': '#F3F4F6',  // Gray 100 — dark: Gray 800
  'ds2026-btn-disabled-outline-text': '#9CA3AF',    // Gray 400 — dark: Gray 600

  // Button / Error / Primary
  'ds2026-btn-error-bg': '#DC2626',        // Red 600 — dark: Red 600
  'ds2026-btn-error-stroke': '#EF4444',    // Red 500 — dark: Red 500
  'ds2026-btn-error-hover-bg': '#B91C1C',  // Red 700 — dark: Red 700
  'ds2026-btn-error-text': '#FFFFFF',      // White — dark: White

  // Button / Error / Outline
  'ds2026-btn-error-outline-bg': '#FEF2F2',                // Red 50 — dark: Red @ 5%
  'ds2026-btn-error-outline-stroke': '#EF4444',            // Red 500 — dark: Red 900
  'ds2026-btn-error-outline-focused-stroke': '#DC2626',    // Red 600 — dark: Red 800
  'ds2026-btn-error-outline-hover-bg': '#FECACA',          // Red 200 — dark: Red @ 15%
  'ds2026-btn-error-outline-text': '#B91C1C',              // Red 700 — dark: Red 500
  'ds2026-btn-error-outline-hover-text': '#B91C1C',        // Red 700 — dark: Red 400

  // Button / Success / Primary
  'ds2026-btn-success-bg': '#16A34A',        // Green 600 — dark: Green 600
  'ds2026-btn-success-stroke': '#22C55E',    // Green 500 — dark: Green 500
  'ds2026-btn-success-hover-bg': '#15803D',  // Green 700 — dark: Green 700
  'ds2026-btn-success-text': '#FFFFFF',      // White — dark: White

  // Button / Success / Outline
  'ds2026-btn-success-outline-bg': '#F0FDF4',              // Green 50 — dark: Green @ 5%
  'ds2026-btn-success-outline-stroke': '#22C55E',          // Green 500 — dark: Green 900
  'ds2026-btn-success-outline-focused-stroke': '#16A34A',  // Green 600 — dark: Green 800
  'ds2026-btn-success-outline-hover-bg': '#BBF7D0',        // Green 200 — dark: Green @ 15%
  'ds2026-btn-success-outline-text': '#15803D',            // Green 700 — dark: Green 500
  'ds2026-btn-success-outline-hover-text': '#15803D',      // Green 700 — dark: Green 400

  // Button / Close icon
  'ds2026-btn-close-hover-bg': '#F3F4F6',     // Gray 100 — dark: Gray 900
  'ds2026-btn-close-color': '#9CA3AF',        // Tertiary text (Gray 400) — dark: same
  'ds2026-btn-close-hover-color': '#6B7280',  // Secondary text (Gray 500) — dark: Gray 300

  // Button / Primary Button Group / Primary
  'ds2026-btn-group-primary-bg': '#FFFFFF',           // White — dark: White @ 0%
  'ds2026-btn-group-primary-text': '#0A9150',         // Basil 600 — dark: Basil 500
  'ds2026-btn-group-primary-hover-bg': '#0A9150',     // Basil 600 — dark: Basil 600
  'ds2026-btn-group-primary-hover-text': '#FFFFFF',   // White — dark: White

  // Button / Primary Button Group / Secondary
  'ds2026-btn-group-secondary-bg': '#FFFFFF',    // White — dark: White @ 1%
  'ds2026-btn-group-secondary-text': '#374151',  // Gray 700 — dark: Gray 400

  // Badges / Gray
  'ds2026-badge-gray-bg': '#F3F4F6',    // Gray 100 — dark: Gray 600
  'ds2026-badge-gray-text': '#374151',  // Gray 700 — dark: Gray 200
  'ds2026-badge-gray-icon': '#6B7280',  // Gray 500 — dark: Gray 200

  // Badges / Primary (Brand Basil)
  'ds2026-badge-primary-bg': '#EDFCF2',    // Basil 50 — dark: Basil @ 15%
  'ds2026-badge-primary-text': '#087443',  // Basil 700 — dark: Basil 700
  'ds2026-badge-primary-icon': '#16B364',  // Basil 500 — dark: Basil 500

  // Badges / Error (Red)
  'ds2026-badge-error-bg': '#FEF2F2',    // Red 50 — dark: Red @ 15%
  'ds2026-badge-error-text': '#B91C1C',  // Red 700 — dark: Red 500
  'ds2026-badge-error-icon': '#DC2626',  // Red 600 — dark: Red 400

  // Badges / Warning (Amber)
  'ds2026-badge-warning-bg': '#FFFBEB',    // Amber 50 — dark: Amber @ 15%
  'ds2026-badge-warning-text': '#B45309',  // Amber 700 — dark: Amber 500
  'ds2026-badge-warning-icon': '#D97706',  // Amber 600 — dark: Amber 400

  // Badges / Success (Green)
  'ds2026-badge-success-bg': '#F0FDF4',    // Green 50 — dark: Green @ 15%
  'ds2026-badge-success-text': '#15803D',  // Green 700 — dark: Green 500
  'ds2026-badge-success-icon': '#16A34A',  // Green 600 — dark: Green 400

  // Badges / Cyan
  'ds2026-badge-cyan-bg': '#ECFEFF',    // Cyan 50 — dark: Cyan @ 15%
  'ds2026-badge-cyan-text': '#0E7490',  // Cyan 700 — dark: Cyan 500
  'ds2026-badge-cyan-icon': '#06B6D4',  // Cyan 500 — dark: Cyan 400

  // Badges / Teal (used by the "Closing" sales-stage phase badge)
  'ds2026-badge-teal-bg': '#F0FDFA',    // Teal 50 — dark: Teal @ 15%
  'ds2026-badge-teal-text': '#0F766E',  // Teal 700 — dark: Teal 500
  'ds2026-badge-teal-icon': '#14B8A6',  // Teal 500 — dark: Teal 400
  'ds2026-badge-teal-soft': '#5EEAD0',  // Teal 300 — soft solid fill (early-in-phase chart segments)

  // Badges / Sky
  'ds2026-badge-sky-bg': '#F0F9FF',    // Sky 50 — dark: Sky @ 15%
  'ds2026-badge-sky-text': '#0369A1',  // Sky 700 — dark: Sky 500
  'ds2026-badge-sky-icon': '#0EA5E9',  // Sky 500 — dark: Sky 400
  'ds2026-badge-sky-soft': '#7DD3FC',  // Sky 300 — soft solid fill (early-in-phase chart segments)

  // Badges / Blue
  'ds2026-badge-blue-bg': '#EFF6FF',    // Blue 50 — dark: Blue @ 15%
  'ds2026-badge-blue-text': '#1D4ED8',  // Blue 700 — dark: Blue 500
  'ds2026-badge-blue-icon': '#2563EB',  // Blue 600 — dark: Blue 400

  // Badges / Violet
  'ds2026-badge-violet-bg': '#F5F3FF',    // Violet 50 — dark: Violet @ 15%
  'ds2026-badge-violet-text': '#6D28D9',  // Violet 700 — dark: Violet 500
  'ds2026-badge-violet-icon': '#8B5CF6',  // Violet 500 — dark: Violet 400
  'ds2026-badge-violet-soft': '#C4B5FD',  // Violet 300 — soft solid fill (early-in-phase chart segments)

  // Badges / Purple
  'ds2026-badge-purple-bg': '#FAF5FF',    // Purple 50 — dark: Purple @ 15%
  'ds2026-badge-purple-text': '#7E22CE',  // Purple 700 — dark: Purple 500
  'ds2026-badge-purple-icon': '#A855F7',  // Purple 500 — dark: Purple 400

  // Badges / Pink
  'ds2026-badge-pink-bg': '#FDF2F8',    // Pink 50 — dark: Pink @ 15%
  'ds2026-badge-pink-text': '#BE185D',  // Pink 700 — dark: Pink 500
  'ds2026-badge-pink-icon': '#EC4899',  // Pink 500 — dark: Pink 400

  // Badges / Rose
  'ds2026-badge-rose-bg': '#FFF1F2',    // Rose 50 — dark: Rose @ 15%
  'ds2026-badge-rose-text': '#BE123C',  // Rose 700 — dark: Rose 500
  'ds2026-badge-rose-icon': '#F43F5E',  // Rose 500 — dark: Rose 400

  // Badges / Orange
  'ds2026-badge-orange-bg': '#FFF7ED',    // Orange 50 — dark: Orange @ 15%
  'ds2026-badge-orange-text': '#C2410C',  // Orange 700 — dark: Orange 500
  'ds2026-badge-orange-icon': '#F97316',  // Orange 500 — dark: Orange 400

  // Logo
  'ds2026-logo-light': '#FFFFFF',  // White — dark: Gray 800
  'ds2026-logo-dark': '#1F2937',   // Gray 800 — dark: White

  // Sidebar Navigation / Nav Item
  'ds2026-sidebar-nav-default-bg': '#FFFFFF',                       // White — dark: Bg/White/Primary
  'ds2026-sidebar-nav-hover-bg': '#F9FAFB',                         // Gray 50 — dark: White @ 0%
  'ds2026-sidebar-nav-secondary-hover-bg': '#F3F4F6',               // Gray 100 — dark: White @ 5%
  'ds2026-sidebar-nav-hover-text': '#374151',                       // Gray 700 — dark: Gray 300
  'ds2026-sidebar-nav-active-text': '#087443',                      // Basil 700 — dark: Basil 500
  'ds2026-sidebar-nav-active-bg': '#EDFCF2',                        // Basil 50 — dark: Basil 700 @ 12%
  'ds2026-sidebar-nav-focused-bg': '#EDFCF2',                       // Basil 50 — dark: Basil 700 @ 12%
  'ds2026-sidebar-nav-default-text': '#374151',                     // Gray 700 — dark: Gray 300
  'ds2026-sidebar-nav-icon': '#6B7280',                             // Gray 500 — dark: Gray 400
  'ds2026-sidebar-nav-badge-bg': '#087443',                         // Basil 700 — dark: Basil 700
  'ds2026-sidebar-nav-badge-text': '#FFFFFF',                       // White — dark: White

  // Sidebar Navigation / Footer
  'ds2026-sidebar-footer-bg': '#F9FAFB',         // Gray 50 — dark: White @ 3%
  'ds2026-sidebar-footer-title': '#111827',      // Gray 900 — dark: White
  'ds2026-sidebar-footer-subtitle': '#6B7280',   // Gray 500 — dark: Gray 400
  'ds2026-sidebar-footer-btn-text': '#FFFFFF',   // White — dark: White

  // Card
  'ds2026-card-bg': '#FFFFFF',           // White — dark: Gray 950
  'ds2026-card-solid-bg': '#FFFFFF',     // White — dark: #1E2634
  'ds2026-card-secondary-bg': '#F3F4F6', // Gray 100 — dark: White @ 3%
  'ds2026-card-bg-alt': '#FFFFFF',       // Bg/Gray/Primary — dark: Gray 900

  // Alerts / Success
  'ds2026-alert-success-bg': '#F0FDF4',           // Green 50 — dark: 22C55E @ 5%
  'ds2026-alert-success-icon-bg': '#16A34A',      // Green 600 — dark: Green 600
  'ds2026-alert-success-border': '#BBF7D0',       // Green 200 — dark: Green 900
  'ds2026-alert-success-close-icon': '#16A34A',   // Green 600 — dark: Green 600
  'ds2026-alert-success-title': '#166534',        // Green 800 — dark: Green 600
  'ds2026-alert-success-description': '#15803D',  // Green 700 — dark: Green 600
  'ds2026-alert-success-btn-bg': '#16A34A',       // Green 600 — dark: Green 500
  'ds2026-alert-success-btn-hover-bg': '#15803D', // Green 700 — dark: Green 600
  'ds2026-alert-success-btn-color': '#15803D',    // Green 700 — dark: Green 500

  // Alerts / Danger
  'ds2026-alert-danger-bg': '#FEF2F2',            // Red 50 — dark: EF4444 @ 5%
  'ds2026-alert-danger-icon-bg': '#DC2626',       // Red 600 — dark: Red 600
  'ds2026-alert-danger-border': '#FECACA',        // Red 200 — dark: Red 900
  'ds2026-alert-danger-close-icon': '#B91C1C',    // Red 700 — dark: Red 700
  'ds2026-alert-danger-title': '#991B1B',         // Red 800 — dark: Red 500
  'ds2026-alert-danger-description': '#B91C1C',   // Red 700 — dark: Red 500
  'ds2026-alert-danger-btn-bg': '#DC2626',        // Red 600 — dark: Red 500
  'ds2026-alert-danger-btn-hover-bg': '#B91C1C',  // Red 700 — dark: Red 600
  'ds2026-alert-danger-btn-color': '#B91C1C',     // Red 700 — dark: Red 500

  // Alerts / Info
  'ds2026-alert-info-bg': '#F0F9FF',           // Sky 50 — dark: 0EA5E9 @ 5%
  'ds2026-alert-info-icon-bg': '#0EA5E9',      // Sky 500 — dark: Sky 600
  'ds2026-alert-info-border': '#BAE6FD',       // Sky 200 — dark: Sky 900
  'ds2026-alert-info-close-icon': '#0284C7',   // Sky 600 — dark: Sky 600
  'ds2026-alert-info-title': '#075985',        // Sky 800 — dark: Sky 500
  'ds2026-alert-info-description': '#0369A1',  // Sky 700 — dark: Sky 500
  'ds2026-alert-info-btn-bg': '#0284C7',       // Sky 600 — dark: Sky 500
  'ds2026-alert-info-btn-hover-bg': '#0369A1', // Sky 700 — dark: Sky 600
  'ds2026-alert-info-btn-color': '#0369A1',    // Sky 700 — dark: Sky 500

  // Alerts / Warning
  'ds2026-alert-warning-bg': '#FEFCE8',           // Yellow 50 — dark: EAB308 @ 5%
  'ds2026-alert-warning-icon-bg': '#EAB308',      // Yellow 500 — dark: Yellow 600
  'ds2026-alert-warning-border': '#FEF08A',       // Yellow 200 — dark: Yellow 900
  'ds2026-alert-warning-close-icon': '#CA8A04',   // Yellow 600 — dark: Yellow 600
  'ds2026-alert-warning-title': '#854D0E',        // Yellow 800 — dark: Yellow 500
  'ds2026-alert-warning-description': '#A16207',  // Yellow 700 — dark: Yellow 500
  'ds2026-alert-warning-btn-bg': '#CA8A04',       // Yellow 600 — dark: Yellow 500
  'ds2026-alert-warning-btn-hover-bg': '#A16207', // Yellow 700 — dark: Yellow 600
  'ds2026-alert-warning-btn-color': '#CA8A04',    // Yellow 600 — dark: Yellow 500

  // Alerts / Default
  'ds2026-alert-default-bg': '#F9FAFB',           // Gray 50 — dark: 6B7280 @ 10%
  'ds2026-alert-default-icon-bg': '#6B7280',      // Gray 500 — dark: Gray 500
  'ds2026-alert-default-border': '#E5E7EB',       // Gray 200 — dark: Gray 900
  'ds2026-alert-default-close-icon': '#4B5563',   // Gray 600 — dark: Gray 600
  'ds2026-alert-default-title': '#1F2937',        // Gray 800 — dark: Gray 300
  'ds2026-alert-default-description': '#374151',  // Gray 700 — dark: Gray 400
  'ds2026-alert-default-btn-bg': '#4B5563',       // Gray 600 — dark: Gray 500
  'ds2026-alert-default-btn-hover-bg': '#374151', // Gray 700 — dark: Gray 600
  'ds2026-alert-default-btn-color': '#374151',    // Gray 700 — dark: Gray 500

  // Status / Gray
  'ds2026-status-gray-light-bg': '#F3F4F6',     // Gray 100 — dark: White @ 5%
  'ds2026-status-gray-light-text': '#374151',   // Gray 700 — dark: White @ 80%
  'ds2026-status-gray-solid-bg': '#9CA3AF',     // Gray 400 — dark: White @ 5%
  'ds2026-status-gray-solid-text': '#FFFFFF',   // White — dark: White @ 80%

  // Status / Primary
  'ds2026-status-primary-light-bg': '#EDFCF2',            // Basil 50 — dark: 087443 @ 15%
  'ds2026-status-primary-light-secondary-bg': '#D2F9DE',  // Basil 100 — dark: 087443 @ 20%
  'ds2026-status-primary-light-text': '#087443',          // Basil 700 — dark: Basil 600
  'ds2026-status-primary-solid-bg': '#087443',            // Basil 700 — dark: Basil 600
  'ds2026-status-primary-solid-text': '#FFFFFF',          // White — dark: White

  // Status / Error
  'ds2026-status-error-light-bg': '#FEF2F2',     // Red 50 — dark: F04438 @ 15%
  'ds2026-status-error-light-text': '#B91C1C',   // Red 700 — dark: Red 500
  'ds2026-status-error-light-icon': '#EF4444',   // Red 500 — dark: Red 500
  'ds2026-status-error-solid-bg': '#EF4444',     // Red 500 — dark: Red 500
  'ds2026-status-error-solid-text': '#FFFFFF',   // White — dark: White

  // Status / Warning
  'ds2026-status-warning-light-bg': '#FEFCE8',     // Yellow 50 — dark: F79009 @ 15%
  'ds2026-status-warning-light-text': '#CA8A04',   // Yellow 600 — dark: Orange 400
  'ds2026-status-warning-solid-bg': '#EAB308',     // Yellow 500 — dark: Yellow 500
  'ds2026-status-warning-solid-text': '#FFFFFF',   // White — dark: White

  // Status / Success
  'ds2026-status-success-light-bg': '#F0FDF4',     // Green 50 — dark: Green 500 @ 15%
  'ds2026-status-success-light-text': '#16A34A',   // Green 600 — dark: Green 500
  'ds2026-status-success-solid-bg': '#22C55E',     // Green 500 — dark: Green 500
  'ds2026-status-success-solid-text': '#FFFFFF',   // White — dark: White

  // Status / Dark
  'ds2026-status-dark-light-bg': '#6B7280',              // Gray 500 — dark: White @ 5%
  'ds2026-status-dark-light-text': '#FFFFFF',            // White — dark: White
  'ds2026-status-dark-light-secondary-text': '#374151',  // Gray 700 — dark: White
  'ds2026-status-dark-solid-bg': '#1F2937',              // Gray 800 — dark: White @ 15%
  'ds2026-status-dark-solid-text': '#FFFFFF',            // White — dark: White

  // Input
  'ds2026-input-bg': '#FFFFFF',                       // White — dark: White @ 5%
  'ds2026-input-label-text': '#374151',               // Text Color (Gray 700) — dark: Gray 400
  'ds2026-input-disabled-text': '#D1D5DB',            // Gray 300 — dark: White @ 15%
  'ds2026-input-placeholder-text': '#9CA3AF',         // Gray 400 — dark: Gray 500
  'ds2026-input-brand-focus-border': '#087443',       // Basil 700 — dark: Basil 600
  'ds2026-input-error-focus-border': '#EF4444',       // Red 500 — dark: Red 700
  'ds2026-input-success-focus-border': '#22C55E',     // Green 500 — dark: Green 700
  'ds2026-input-error': '#EF4444',                    // Red 500 — dark: Red 600
  'ds2026-input-success': '#22C55E',                  // Green 500 — dark: Green 600

  // Tab
  'ds2026-tab-bg': '#F3F4F6',                    // Gray 100 — dark: Gray 900
  'ds2026-tab-active-bg': '#FFFFFF',             // White — dark: Gray 800
  'ds2026-tab-secondary-active-bg': '#EDFCF2',   // Basil 50 — dark: 087443 @ 20%

  // Schedule list
  'ds2026-schedule-bg': '#FFFFFF',          // White — dark: #101828
  'ds2026-schedule-active-bg': '#F9FAFB',   // Gray 50 — dark: White @ 3%

  // Checkbox
  'ds2026-checkbox-bg': '#FFFFFF',            // White — dark: White @ 0%
  'ds2026-checkbox-checked-border': '#087443', // Brand Color — dark: Basil 500
  'ds2026-checkbox-checked-bg': '#087443',     // Brand Color — dark: Basil 500
  'ds2026-checkbox-checked-color': '#FFFFFF',  // White — dark: White

  // Scrollbar
  'ds2026-scrollbar-color': '#E5E7EB',  // Gray 200 — dark: Gray 700

  // Datepicker
  'ds2026-datepicker-bg': '#FFFFFF',                  // White — dark: Gray 800
  'ds2026-datepicker-selected-date-bg': '#F3F4F6',    // Gray 100 — dark: White @ 5%

  // Dropdowns
  'ds2026-dropdown-bg': '#FFFFFF',            // White — dark: Gray 800
  'ds2026-dropdown-hover-bg': '#F3F4F6',      // Gray 100 — dark: Gray 700
  'ds2026-dropdown-secondary-bg': '#FFFFFF',  // White — dark: #1E2635
  'ds2026-dropdown-divider': '#F3F4F6',       // Border/Primary (Gray 100) — dark: #353C49

  // Calendar
  'ds2026-calendar-disabled-date': '#9CA3AF',  // Tertiary text (Gray 400) — dark: White @ 30%

  // Calendar / Selected Date — badge + bg per category
  'ds2026-cal-primary-badge': '#087443',  // Brand Color (Basil 700) — dark: Basil 500
  'ds2026-cal-primary-bg': '#EDFCF2',     // Basil 50 — dark: Basil 100
  'ds2026-cal-success-badge': '#22C55E',  // Green 500 — dark: Green 500
  'ds2026-cal-success-bg': '#F0FDF4',     // Green 50 — dark: Green 100
  'ds2026-cal-warning-badge': '#F97316',  // Orange 500 — dark: Orange 500
  'ds2026-cal-warning-bg': '#FFF7ED',     // Orange 50 — dark: Orange 100
  'ds2026-cal-danger-badge': '#EF4444',   // Red 500 — dark: Red 500
  'ds2026-cal-danger-bg': '#FEF2F2',      // Red 50 — dark: Red 100
  'ds2026-cal-info-badge': '#0EA5E9',     // Sky 500 — dark: Sky 500
  'ds2026-cal-info-bg': '#F0F9FF',        // Sky 50 — dark: Sky 100
  'ds2026-cal-text': '#374151',           // Text Color (Gray 700) — dark: Gray 700
  'ds2026-cal-more-menu': '#6B7280',      // Secondary text (Gray 500) — dark: Tertiary text
  'ds2026-cal-dropdown-bg': '#FFFFFF',    // White — dark: Gray 800

  // Kanban Task
  'ds2026-kanban-card-bg': '#FFFFFF',      // White — dark: White @ 5%
  'ds2026-kanban-dropdown-bg': '#FFFFFF',  // White — dark: Gray 800

  // Switch
  'ds2026-switch-default-bg': '#F3F4F6',  // Border/Primary (Gray 100) — dark: White @ 10%
  'ds2026-switch-custom-bg': '#1F2937',   // Title color (Gray 800) — dark: White @ 10%

  // Tooltip
  'ds2026-tooltip-text': '#374151',  // Text Color (Gray 700) — dark: White

  // Popovers
  'ds2026-popover-title-bg': '#F3F4F6',  // Gray 100 — dark: Gray 800
  'ds2026-popover-border': '#E5E7EB',    // Border/Secondary (Gray 200) — dark: Gray 700

  // Skeleton
  'ds2026-skeleton-gradient-100': '#E5E7EB',  // Gray 200 — dark: White @ 15%
  'ds2026-skeleton-gradient-200': '#F9FAFB',  // Gray 50 — dark: White @ 5%
  'ds2026-skeleton-gradient-300': '#E5E7EB',  // Gray 200 — dark: White @ 15%

  // Modal — action button backgrounds per intent
  'ds2026-modal-success-btn-bg': '#16A34A',  // Green 600 — dark: Green 500
  'ds2026-modal-info-btn-bg': '#0284C7',     // Sky 600 — dark: Sky 500
  'ds2026-modal-warning-btn-bg': '#CA8A04',  // Yellow 600 — dark: Yellow 500
  'ds2026-modal-error-btn-bg': '#DC2626',    // Red 600 — dark: Red 500

  // Section Title — pill badge
  'ds2026-section-title-badge-color': '#087443',                 // Basil 700 — dark: Basil 600
  'ds2026-section-title-badge-bg': 'rgba(8, 116, 67, 0.05)',     // Basil 700 @ 5% — dark: Basil 700 @ 15%

  // Mobile nav menu button
  'ds2026-mobile-nav-icon': '#374151',         // Text Color (Gray 700) — dark: Gray 300
  'ds2026-mobile-nav-bg': '#FFFFFF',           // White — dark: Gray 950
  'ds2026-mobile-nav-hover-bg': '#F3F4F6',     // Gray 100 — dark: Gray 900

  // Footer
  'ds2026-footer-card-bg': '#FFFFFF',  // Background/Gray/Primary — dark: Gray 900
  'ds2026-footer-bg': '#FFFFFF',       // Background/White/Primary — dark: Gray 950
}

export const fontSize = {
  // Gap size that Tailwind doesn't provide by default
  xxs: ['11px', { lineHeight: '14px' }],
  // Tailwind defaults remain: xs=12px, sm=14px, base=16px, lg=18px, etc.
  // Override the full scale in Phase 2 after visual audit with Laura.
}
